// A table that scrolls sideways inside its own box (phones) must be reachable by keyboard (axe:
// scrollable-region-focusable, QA-299). Put `ref={scrollFocus}` on the wrapper: while its content is
// wider than the box, the box is a focusable, named region (its data-region, its table's name, or the
// heading just before it); when everything fits it is a plain box again, so desktops get no extra tab stop.
import './scroll-focus.css';

function baseName(el: HTMLElement, outer: Element | null): string {
  if (el.dataset.region) return el.dataset.region; // a wrapper that names itself (data-region)
  const table = el.querySelector('table');
  const own = table?.getAttribute('aria-label') ?? table?.querySelector('caption')?.textContent;
  if (own) return own.trim();
  for (let s = el.previousElementSibling; s; s = s.previousElementSibling)
    if (/^H[1-6]$/.test(s.tagName) || s.querySelector('h2, h3, h4'))
      return (/^H[1-6]$/.test(s.tagName) ? s : s.querySelector('h2, h3, h4')!).textContent!.trim();
  return outer?.getAttribute('aria-label') ?? 'Table';
}

// A panel named like its table ("Members" around "Members") would make two landmarks of one name
// (axe landmark-unique): the box then says it is the table.
function nameOf(el: HTMLElement): string {
  const outer = el.parentElement?.closest('[aria-label]') ?? null;
  const name = baseName(el, outer);
  return outer?.getAttribute('aria-label')?.trim() === name ? `${name} table` : name;
}

export function scrollFocus(el: HTMLElement | null): (() => void) | undefined {
  if (!el) return undefined;
  const update = () => {
    // Only a box that really scrolls (overflow-x auto or scroll) and is wider inside than out: content
    // that merely pokes out of a plain box isn't scrollable, and must not add a tab stop.
    const scrolls = /auto|scroll/.test(getComputedStyle(el).overflowX);
    if (scrolls && el.scrollWidth > el.clientWidth + 1) {
      el.tabIndex = 0;
      el.dataset.scrollFocus = ''; // its focus ring (scroll-focus.css), whatever the wrapper's class
      el.setAttribute('role', 'region');
      el.setAttribute('aria-label', nameOf(el));
    } else {
      el.removeAttribute('tabindex');
      delete el.dataset.scrollFocus;
      el.removeAttribute('role');
      el.removeAttribute('aria-label');
    }
  };
  const ro = new ResizeObserver(update);
  ro.observe(el);
  const t = el.querySelector('table');
  if (t) ro.observe(t);
  update();
  return () => ro.disconnect();
}
